import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useOrg } from '../contexts/OrgContext';
import Header from '../components/Header';
import { BUILTIN_TYPE_META, formatType } from '../data/builtinTemplates';

export default function Dashboard({ queueCount, syncing }) {
  const { membership, isAdmin } = useOrg();
  const [recent, setRecent] = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);

  useEffect(() => {
    if (!membership?.org_id) return;
    const templatesCacheKey = `gc_templates_${membership.org_id}`;

    const cachedTemplates = (() => { try { return JSON.parse(localStorage.getItem(templatesCacheKey)); } catch { return null; } })();
    if (cachedTemplates) setCustomTemplates(cachedTemplates);

    supabase
      .from('submissions')
      .select('id, checklist_type, overall_status, driver_name, created_at, assets(name, identifier)')
      .eq('org_id', membership.org_id)
      .order('created_at', { ascending: false })
      .limit(5)
      .then(({ data }) => { if (data) setRecent(data); });

    supabase
      .from('checklist_templates')
      .select('id, name, type')
      .eq('org_id', membership.org_id)
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (data && !error) {
          setCustomTemplates(data);
          try { localStorage.setItem(templatesCacheKey, JSON.stringify(data)); } catch {}
        }
      });
  }, [membership?.org_id]);

  const builtins = Object.entries(BUILTIN_TYPE_META);

  return (
    <div className="min-h-screen bg-gray-900">
      <Header queueCount={queueCount} syncing={syncing} />

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Start a check */}
        <section>
          <h2 className="text-white font-semibold text-base mb-3">Start a Check</h2>
          <div className="space-y-2.5">
            {builtins.map(([type, meta]) => (
              <Link
                key={type}
                to={`/checklist/${type}`}
                className="flex items-center gap-4 bg-gray-800 hover:bg-gray-750 border border-gray-700 rounded-xl p-4 transition-colors"
              >
                <div className="bg-orange-500/20 rounded-lg w-12 h-12 flex items-center justify-center shrink-0">
                  <span className="text-orange-400 font-bold text-sm">{meta.label.charAt(0)}</span>
                </div>
                <div>
                  <p className="text-white font-medium">{meta.label}</p>
                  <p className="text-gray-400 text-sm">{meta.description}</p>
                </div>
                <span className="ml-auto text-gray-600">›</span>
              </Link>
            ))}

            {customTemplates.map((tpl) => (
              <Link
                key={tpl.id}
                to={`/checklist/${tpl.id}`}
                className="flex items-center gap-4 bg-gray-800 hover:bg-gray-750 border border-gray-700 rounded-xl p-4 transition-colors"
              >
                <div className="bg-orange-500/20 rounded-lg w-12 h-12 flex items-center justify-center shrink-0">
                  <span className="text-orange-400 font-bold text-sm">C</span>
                </div>
                <div>
                  <p className="text-white font-medium">{tpl.name}</p>
                  <p className="text-gray-400 text-sm">Custom template</p>
                </div>
                <span className="ml-auto text-gray-600">›</span>
              </Link>
            ))}
          </div>
        </section>

        {/* Quick links */}
        <section className="flex gap-3">
          <Link
            to="/submissions"
            className="flex-1 bg-gray-800 border border-gray-700 rounded-xl p-4 text-center hover:border-gray-600 transition-colors"
          >
            <p className="text-white font-medium text-sm">Submissions</p>
            <p className="text-gray-500 text-xs mt-0.5">View history</p>
          </Link>
          {isAdmin && (
            <Link
              to="/admin"
              className="flex-1 bg-gray-800 border border-gray-700 rounded-xl p-4 text-center hover:border-gray-600 transition-colors"
            >
              <p className="text-white font-medium text-sm">Admin</p>
              <p className="text-gray-500 text-xs mt-0.5">Manage org</p>
            </Link>
          )}
        </section>

        {/* Recent submissions */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white font-semibold text-base">Recent</h2>
            <Link to="/submissions" className="text-orange-400 text-sm hover:text-orange-300">
              View all
            </Link>
          </div>

          {recent.length === 0 ? (
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 text-center">
              <p className="text-gray-500 text-sm">No submissions yet. Start a check above.</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {recent.map((s) => (
                <Link
                  key={s.id}
                  to={`/submissions/${s.id}`}
                  className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded-xl p-4 hover:border-gray-600 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate">
                      {formatType(s.checklist_type)}
                    </p>
                    <p className="text-gray-500 text-xs">
                      {s.driver_name} · {new Date(s.created_at).toLocaleDateString('en-AU')}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
